import ApiService from "./api-service";
import { FileLimits } from "../util/constants";

const NO_TEMPLATE = "No template";

const ManageService = {
  async getGeneral(ShortCode) {
    const body = {
      query: `query {
      organisation {
        currencySymbols
        chaseInvoicesPriorToDueDate
        invoiceThreshold
        includeUnpaidInvoices
        threshold {
          creditLimit
          notifications {
            percentageLimit
          }
        }
        minimumDaysBetweenChase {
          days
          enabled
        }
        features {
          customer_statements_ageing_buckets_enabled
          chase_disputed_invoices
          chase_direct_debit
          exclude_negative_net_balance
          exclude_unallocated_payments
        }
        statement {
          contactDetails
          terms
          logo
          alignment
          previewContactID
        }
      }
    }
    `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async getInvoicesOnDifferentSchedules(ShortCode) {
    const body = {
      query: `query {
        organisation {
          invoicesOnDifferentSchedules {
            _id
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async getEmail(ShortCode) {
    const body = {
      query: `query {
        organisation {
          users {
            id
            FirstName
            LastName
            EmailAddress
            verified
            forewarn
            signature
            nylas {
              isConnected
              errors
            }
          }
          fontSize
          fontFace
          senders {
            regular {
              from {
                id
              }
              cc {
                id
              }
            }
            escalated {
              from {
                id
              }
              cc {
                id
              }
            }
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation;
  },
  async checkInvoicesBelowAmount({ amount, ShortCode }) {
    const body = {
      query: `query ($amount: Float) {
        organisation {
          invoices(filter: {
            below: $amount
          }) {
            totalCount
            nodes {
              _id
            }
          }
        }
      }
      `,
      variables: {
        amount: parseFloat(amount),
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.invoices.totalCount;
  },
  async setChasePriorToEPD({ value, ShortCode }) {
    const body = {
      query: `mutation($value: Boolean!) {
        chaseInvoicesPriorToDueDate(value: $value) {
          chaseInvoicesPriorToDueDate
        }
      }
      `,
      variables: {
        value,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.chaseInvoicesPriorToDueDate.chaseInvoicesPriorToDueDate;
  },
  async setMinimumDaysBetweenChase({ days, enabled, ShortCode }) {
    const body = {
      query: `mutation setCustomerLevelChasing($days: Int!, $enabled: Boolean!) {
        setCustomerLevelChasing(days: $days, enabled: $enabled)
      }`,
      variables: {
        days,
        enabled,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data;
  },
  async setIncludeUnpaidInvoices({ enabled, ShortCode }) {
    const body = {
      query: `mutation setIncludeUnpaidInvoices($enabled: Boolean!) {
        setIncludeUnpaidInvoices(enabled: $enabled)
      }`,
      variables: {
        enabled,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data;
  },
  async setInvoiceThreshold({ amount, ShortCode }) {
    if (amount === null) return ManageService.unsetInvoiceThreshold(ShortCode);
    const body = {
      query: `mutation($amount: Float!) {
        setInvoiceThreshold(amount: $amount) {
          invoiceThreshold
        }
      }
      `,
      variables: {
        amount,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setInvoiceThreshold.invoiceThreshold;
  },
  async unsetInvoiceThreshold(ShortCode) {
    const body = {
      query: `mutation {
        unsetInvoiceThreshold {
          invoiceThreshold
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.unsetInvoiceThreshold.invoiceThreshold;
  },
  async addSender(ShortCode, values) {
    const body = {
      query: `mutation($email: NonEmptyString!, $firstName: String!, $lastName: String, $signature: String) {
        addSender(
          emailAddress: $email,
          firstName: $firstName,
          lastName: $lastName,
          signature: $signature,
        ) {
          users {
            id
          }
        }
      }
      `,
      variables: values,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.addSender.users;
  },
  async editSender(ShortCode, values) {
    const body = {
      query: `mutation($id: NonEmptyString!, $firstName: String, $lastName: String, $forewarn: Boolean, $signature: String) {
        editSender(
          senderId: $id,
          firstName: $firstName,
          lastName: $lastName,
          forewarn: $forewarn,
          signature: $signature,
        ) {
          users {
            id
          }
        }
      }
      `,
      variables: values,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.editSender.users;
  },
  async removeSender(ShortCode, id) {
    const body = {
      query: `mutation($id: NonEmptyString!) {
        removeSender(
          senderId: $id,
        ) {
          users {
            id
          }
        }
      }
      `,
      variables: { id },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.removeSender.users;
  },
  async setPrimarySender(ShortCode, variables) {
    const body = {
      query: `mutation($escalation: NonEmptyString!, $id: NonEmptyString!) {
        setPrimarySender(escalation: $escalation, senderId: $id) {
          senders {
            regular {
              from {
                id
              }
            }
            escalated {
              from {
                id
              }
            }
          }
        }
      }
      `,
      variables,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setPrimarySender.senders;
  },
  async toggleCCSender(ShortCode, variables) {
    const body = {
      query: `mutation($escalation: NonEmptyString!, $id: NonEmptyString!, $active: Boolean!) {
        toggleCCSender(escalation: $escalation, senderId: $id, active: $active) {
          ShortCode
          senders {
            regular {
              cc {
                id
              }
            }
            escalated {
              cc {
                id
              }
            }
          }
        }
      }
      `,
      variables,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.toggleCCSender.senders;
  },
  async editStatement(ShortCode, values) {
    const body = {
      query: `mutation($terms: String, $contactDetails: String, $ageingBucketsEnabled: Boolean!) {
        upsertStatement(terms: $terms, contactDetails: $contactDetails)
        {
          statement {
            terms
            contactDetails
          }
        }
        setCustomerStatementsAgeingBucketsEnabled(enable: $ageingBucketsEnabled)
      }`,
      variables: values,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);

    if (!data.upsertStatement?.statement) {
      throw new Error("Failed to update statement details");
    }

    if (data.setCustomerStatementsAgeingBucketsEnabled !== true) {
      throw new Error("Failed to update ageing buckets setting");
    }

    return data.upsertStatement.statement;
  },
  async deleteStatementLogo(ShortCode) {
    const body = {
      query: `mutation {
        deleteStatementLogo {
          statement {
            logo
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.deleteStatementLogo.statement.logo;
  },
  async uploadStatementLogo(ShortCode, file, alignment = "left") {
    if (file.size > FileLimits.uploadSizeLimitBytes)
      throw new Error(
        `File size needs to be ${FileLimits.uploadSizeLimitBytes / 1024}KB or less.`,
      );

    const ops = {
      query: `mutation($file: Upload, $alignment: String!) {
        upsertStatement(logo: $file, alignment: $alignment){
          statement {
            logo
            alignment
          }
        }
      }
      `,
      variables: { file: null, alignment },
    };
    const map = {
      0: ["variables.file"],
    };

    const formData = new FormData();
    formData.append("operations", JSON.stringify(ops));
    formData.append("map", JSON.stringify(map));
    formData.append(0, file);

    const data = await ApiService.orgEndpoint(ShortCode, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).post("/", formData);
    return data.upsertStatement.statement.logo;
  },
  async editLogoAlignment(ShortCode, alignment) {
    const body = {
      query: `mutation($alignment: String) {
        upsertStatement(alignment: $alignment){
          statement {
            alignment
          }
        }
      }
      `,
      variables: {
        alignment,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.upsertStatement.statement;
  },
  async uploadSignatureImage(ShortCode, file) {
    if (file.size > FileLimits.uploadSizeLimitBytes)
      throw new Error(
        `File size needs to be ${FileLimits.uploadSizeLimitBytes / 1024}KB or less.`,
      );

    const ops = {
      query: `mutation($file: Upload!) {
        uploadSignatureImage(image: $file)
      }
      `,
      variables: { file: null },
    };
    const map = {
      0: ["variables.file"],
    };

    const formData = new FormData();
    formData.append("operations", JSON.stringify(ops));
    formData.append("map", JSON.stringify(map));
    formData.append(0, file);

    const data = await ApiService.orgEndpoint(ShortCode, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).post("/", formData);
    return data.uploadSignatureImage;
  },
  async useCurrencySymbol(ShortCode, use) {
    const body = {
      query: `mutation($use: Boolean!) {
        useCurrencySymbol(use: $use) {
          currencySymbols
        }
      }
      `,
      variables: { use },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.useCurrencySymbol.currencySymbols;
  },
  async setFontSize(ShortCode, fontSize) {
    const body = {
      query: `mutation($fontSize: Int!) {
        setFontSize(fontSize: $fontSize) {
          ShortCode
          fontSize
        }
      }
      `,
      variables: { fontSize },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setFontSize.fontSize;
  },
  async setFontFace(ShortCode, fontFace) {
    const body = {
      query: `mutation($fontFace: NonEmptyString!) {
        setFontFace(fontFace: $fontFace) {
          ShortCode
          fontFace
        }
      }
      `,
      variables: { fontFace },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setFontFace.fontFace;
  },
  async createCheckoutHostedPage(
    shortCode,
    { planName, currency, period, moduleName },
  ) {
    const body = {
      query: /* GraphQL */ `
        mutation createCheckoutHostedPage(
          $planName: String!
          $currency: String!
          $period: String!
          $moduleName: String
        ) {
          createCheckoutHostedPage(
            planName: $planName
            currency: $currency
            period: $period
            moduleName: $moduleName
          )
        }
      `,
      variables: { planName, currency, period, moduleName },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.createCheckoutHostedPage;
  },
  async persistModuleSubscriptionRecord(
    shortCode,
    { planName, currency, period },
  ) {
    const body = {
      query: /* GraphQL */ `
        mutation persistModuleSubscriptionRecord(
          $planName: String!
          $currency: String!
          $period: String!
        ) {
          persistModuleSubscriptionRecord(
            planName: $planName
            currency: $currency
            period: $period
          )
        }
      `,
      variables: { planName, currency, period },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.persistModuleSubscriptionRecord;
  },
  async getDomains(ShortCode) {
    const body = {
      query: `query {
        organisation {
          domains {
            name
            isValid
            lastVerificationDate
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.domains;
  },
  async addDomain(shortCode, domain) {
    const body = {
      query: `mutation($name: NonEmptyString!) {
        addDomain(name: $name) {
          name
        }
      }`,
      variables: { name: domain },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
  },
  async verifyDomain(shortCode, domain) {
    const body = {
      query: `mutation($name: NonEmptyString!) {
        verifyDomain(name: $name) {
          isValid
          cached
          type
          name
          value
        }
      }`,
      variables: { name: domain },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.verifyDomain;
  },
  async deleteDomain(shortCode, domain) {
    const body = {
      query: `mutation($name: NonEmptyString!) {
        deleteDomain(name: $name)
      }`,
      variables: { name: domain },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
  },
  async getEmailLogging(ShortCode) {
    const body = {
      query: `query {
        organisation {
          emailLogging {
            cc
            bcc
          }
        }
      }
      `,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.emailLogging;
  },
  async setEmailLogging(shortCode, cc, bcc) {
    const body = {
      query: `mutation($cc: String, $bcc: String) {
        setEmailLogging(cc: $cc, bcc: $bcc)
      }`,
      variables: { cc, bcc },
    };

    await ApiService.orgEndpoint(shortCode).post("/", body);
  },
  async setChaseDisputedInvoices({ value, ShortCode }) {
    const body = {
      query: `mutation($value: Boolean!) {
      setChaseDisputedInvoicesEnabled(enable: $value)
    }`,
      variables: {
        value,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setChaseDisputedInvoicesEnabled;
  },
  async setChaseDebitOrders({ value, ShortCode }) {
    const body = {
      query: `mutation($value: Boolean!) {
      setChaseDebitOrdersEnabled(enable: $value)
    }`,
      variables: {
        value,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setChaseDebitOrdersEnabled;
  },
  async setChaseNegativeNetBalance({ value, ShortCode }) {
    const body = {
      query: `mutation($value: Boolean!) {
      setChaseNegativeNetBalanceEnabled(enable: $value)
    }`,
      variables: {
        value,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setChaseNegativeNetBalanceEnabled;
  },
  async setChaseUnallocatedPayments({ value, ShortCode }) {
    const body = {
      query: `mutation($value: Boolean!) {
      setChaseUnallocatedPaymentsEnabled(enable: $value)
    }`,
      variables: {
        value,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.setChaseUnallocatedPaymentsEnabled;
  },
};

export default ManageService;
export { NO_TEMPLATE };
